import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { 
  Play, 
  Pause, 
  SkipForward, 
  SkipBack, 
  Volume2, 
  VolumeX,
  Maximize2,
  Music,
  Zap,
  Heart,
  Shuffle,
  Repeat,
  Repeat1,
  ListMusic,
  Rocket,
} from 'lucide-react';
import { usePlayer } from '@/contexts/PlayerContext';
import { useStreamingPayments } from '@/hooks/useStreamingPayments';
import { useAuthor } from '@/hooks/useAuthor';
import { useNWC } from '@/hooks/useNWCContext';
import { useLibrary } from '@/hooks/useLibrary';
import { TrackMenu } from '@/components/TrackMenu';
import { BoostDialog } from '@/components/BoostDialog';
import { formatDuration } from '@/lib/audioUtils';
import { cn } from '@/lib/utils';

interface PlayerBarProps {
  onExpand: () => void;
}

export function PlayerBar({ onExpand }: PlayerBarProps) {
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    togglePlay,
    next,
    previous,
    seek,
    setVolume,
    toggleMute,
    playlist,
  } = usePlayer();

  const { totalSatsSent, isStreaming, satsPerMinute } = useStreamingPayments(currentTrack, isPlaying);
  const { connections } = useNWC();
  const { isLiked, toggleLike } = useLibrary();
  const author = useAuthor(currentTrack?.event.pubkey);
  const metadata = author.data?.metadata;
  const hasLnAddress = !!(metadata?.lud16 || metadata?.lud06);

  const liked = currentTrack ? isLiked(currentTrack.id) : false;
  const [showBoost, setShowBoost] = useState(false);
  
  // Shuffle and repeat state
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState<'off' | 'all' | 'one'>('off');

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const npub = currentTrack ? nip19.npubEncode(currentTrack.event.pubkey) : '';

  const cycleRepeat = () => {
    if (repeat === 'off') setRepeat('all');
    else if (repeat === 'all') setRepeat('one');
    else setRepeat('off');
  };

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      className="fixed bottom-0 left-0 right-0 z-50 glass-strong border-t border-white/5"
    >
      {/* Progress Bar */}
      <div 
        className="absolute top-0 left-0 right-0 h-1 bg-white/5 group cursor-pointer"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const percent = (e.clientX - rect.left) / rect.width;
          seek(percent * duration);
        }}
      >
        <motion.div 
          className="h-full bg-gradient-to-r from-orange-500 to-amber-400 relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 h-3 w-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-orange-500/50" />
        </motion.div>
      </div>

      <div className="flex items-center justify-between px-2 sm:px-4 py-2.5 h-[76px] gap-2">
        {/* Track Info */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink">
          {currentTrack ? (
            <>
              <motion.div 
                className={cn(
                  'h-12 w-12 sm:h-14 sm:w-14 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer',
                  'bg-gradient-to-br from-white/10 to-white/5',
                  'border border-white/10 shadow-lg',
                  'hover:border-white/20 transition-all',
                  isPlaying && 'ring-2 ring-primary/40'
                )}
                onClick={onExpand}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {currentTrack.coverImage ? (
                  <img src={currentTrack.coverImage} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <Music className="h-6 w-6 text-white/30" />
                  </div>
                )}
              </motion.div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{currentTrack.title}</p>
                <Link 
                  to={`/${npub}`}
                  className="text-xs text-muted-foreground hover:text-foreground truncate block transition-colors"
                >
                  {currentTrack.artist}
                </Link>
              </div>
              
              {/* Like & Menu buttons - hidden on small mobile */}
              <div className="hidden xs:flex items-center gap-1">
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => currentTrack && toggleLike(currentTrack.id)}
                  className={cn(
                    'p-1.5 rounded-full transition-colors',
                    liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'
                  )}
                >
                  <Heart className={cn('h-4 w-4', liked && 'fill-current')} />
                </motion.button>
                <TrackMenu track={currentTrack} />
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 sm:gap-3 text-muted-foreground">
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <Music className="h-5 w-5 sm:h-6 sm:w-6 text-white/20" />
              </div>
              <span className="text-sm hidden sm:block">Select a track</span>
            </div>
          )}
        </div>

        {/* Center Controls */}
        <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-0.5 sm:gap-2">
            {/* Shuffle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShuffle(!shuffle)}
              className={cn(
                'h-8 w-8 hidden sm:flex',
                shuffle ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Shuffle className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={previous}
              disabled={!currentTrack || playlist.length <= 1}
              className="h-8 w-8 text-muted-foreground hover:text-foreground disabled:opacity-30"
            >
              <SkipBack className="h-4 w-4" fill="currentColor" />
            </Button>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                size="icon"
                onClick={togglePlay}
                disabled={!currentTrack}
                className={cn(
                  'h-11 w-11 rounded-full',
                  'bg-white text-black hover:bg-white/90',
                  'shadow-lg shadow-white/20',
                  !currentTrack && 'opacity-50'
                )}
              >
                {isPlaying ? (
                  <Pause className="h-5 w-5" fill="currentColor" />
                ) : (
                  <Play className="h-5 w-5 ml-0.5" fill="currentColor" />
                )}
              </Button>
            </motion.div>

            <Button
              variant="ghost"
              size="icon"
              onClick={next}
              disabled={!currentTrack || playlist.length <= 1}
              className="h-8 w-8 text-muted-foreground hover:text-foreground disabled:opacity-30"
            >
              <SkipForward className="h-4 w-4" fill="currentColor" />
            </Button>

            {/* Repeat */}
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleRepeat}
              className={cn(
                'h-8 w-8 hidden sm:flex',
                repeat !== 'off' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {repeat === 'one' ? <Repeat1 className="h-4 w-4" /> : <Repeat className="h-4 w-4" />}
            </Button>
          </div>

          {/* Time Display - hidden on very small screens */}
          <div className="hidden sm:flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className="w-9 text-right tabular-nums">{formatDuration(currentTime)}</span>
            <div className="w-32 sm:w-48 lg:w-64 h-1 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white/40 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="w-9 tabular-nums">{formatDuration(duration)}</span>
          </div>
        </div>

        {/* Right Controls */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Streaming Status with total sent - compact on mobile */}
          {currentTrack && isStreaming && (
            <div className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">
              <motion.span 
                className="relative flex h-1.5 w-1.5 flex-shrink-0"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-orange-500" />
              </motion.span>
              <Zap className="h-3 w-3 flex-shrink-0" />
              <span className="tabular-nums whitespace-nowrap">{totalSatsSent > 0 ? totalSatsSent : satsPerMinute}/m</span>
            </div>
          )}
          
          {/* Boost Button - icon only on mobile */}
          {currentTrack && hasLnAddress && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowBoost(true)}
              className={cn(
                'flex items-center gap-1.5 p-2 sm:px-3 sm:py-1.5 rounded-full text-xs font-medium',
                'bg-gradient-to-r from-purple-500/20 to-pink-500/20',
                'border border-purple-500/30 text-purple-300',
                'hover:from-purple-500/30 hover:to-pink-500/30 transition-all'
              )}
            >
              <Rocket className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
              <span className="hidden sm:inline">Boost</span>
            </motion.button>
          )}

          {/* Queue */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground hidden md:flex"
          >
            <ListMusic className="h-4 w-4" />
          </Button>

          {/* Volume */}
          <div className="hidden md:flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMute}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <Slider
              value={[isMuted ? 0 : volume]}
              max={1}
              step={0.01}
              onValueChange={(v) => setVolume(v[0])}
              className="w-20"
            />
          </div>

          {/* Expand */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onExpand}
            disabled={!currentTrack}
            className="h-8 w-8 text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Boost Dialog */}
      {currentTrack && (
        <BoostDialog 
          track={currentTrack} 
          open={showBoost} 
          onClose={() => setShowBoost(false)} 
        />
      )}
    </motion.div>
  );
}
